import React, { useState } from 'react';
import './WateringCanV2.css';
import { typeFlowerColors } from '../constants/constants';

export function WateringCanV2({ tutorData }) {
  const [hoveredCan, setHoveredCan] = useState(null);

  if (!tutorData || !tutorData.processedData) {
    return null;
  }

  const { processedData } = tutorData;
 
  const activeTalkMoves = Object.entries(processedData)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  if (activeTalkMoves.length === 0) {
    return null;
  }

  // Calculate sizes - largest gets 100%, others scale proportionally
  const maxValue = Math.max(...activeTalkMoves.map(([_, count]) => count));
  const minScale = 0.7;
  const maxScale = 1.0;

  const getCanScale = (count) => {
    if (maxValue === 0) return minScale;
    const scale = count / maxValue;
    return minScale + (maxScale - minScale) * scale;
  };

  // Get color for talk move type
  const getColor = (type) => {
    const colorClass = typeFlowerColors[type] || 'whiteflower';
    const colorMap = {
      'redflower': '#f07d7f',
      'greenflower': '#aae76d',
      'blueflower': '#79aaee',
      'yellowflower': '#eed571',
      'purpleflower': '#c26be4',
      'blackflower': '#333',
      'whiteflower': '#e0e0e0'
    };
    return colorMap[colorClass] || '#e0e0e0';
  };

  // Format talk move type name for display
  const formatTypeName = (type) => {
    return type
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/^./, str => str.toUpperCase());
  };

  // Get positioning and rotation for each can based on total count
  const getCanPositioning = (index, totalCans) => {
    if (totalCans === 1) {
      // Single can - centered, right-sided funnel
      return {
        scaleX: 1,
        translateY: 0,
        order: 0
      };
    } else if (totalCans === 2) {
      // Two cans - one right-sided, one left-sided
      return index === 0 
        ? { scaleX: 1, translateY: 0, order: 0 }     // Left can - right-sided funnel
        : { scaleX: -1, translateY: 0, order: 1 };   // Right can - left-sided funnel (flipped)
    } else if (totalCans === 3) {
      // Three cans - 1st: right-sided, center: left-sided elevated, 3rd: left-sided
      if (index === 0) {
        return { scaleX: 1, translateY: 0, order: 0 };      // 1st can - right-sided funnel
      } else if (index === 1) {
        return { scaleX: -1, translateY: -20, order: 1 };   // Center can - left-sided funnel (flipped), elevated
      } else {
        return { scaleX: -1, translateY: 0, order: 2 };     // 3rd can - left-sided funnel (flipped)
      }
    }
    return { scaleX: 1, translateY: 0, order: index };
  };

  const totalCans = Math.min(activeTalkMoves.length, 3);

  return (
    <div className="watering-can-container" role="img" aria-label="Tutor talk moves visualization">
      <div className="watering-cans-wrapper">
        {activeTalkMoves.slice(0, 3).map(([type, count], index) => {
          const scale = getCanScale(count);
          const color = getColor(type);
          const numDrops = Math.min(Math.ceil(count / 3), 8);
          const positioning = getCanPositioning(index, totalCans);
          
          return (
            <div
              key={type}
              className="watering-can-item"
              style={{
                animationDelay: `${index * 0.1}s`,
                order: positioning.order
              }}
            >
              <div 
                className="watering-can-svg-wrapper" 
                data-flipped={positioning.scaleX === -1 ? 'true' : 'false'}
                onMouseEnter={() => setHoveredCan(index)}
                onMouseLeave={() => setHoveredCan(null)}
                style={{
                  transform: `scale(${scale}) scaleX(${positioning.scaleX}) translateY(${positioning.translateY}px)`,
                }}
              >
                {/* SVG Watering Can - Flipped to have spout on the right */}
                <svg 
                  className="watering-can-svg" 
                  viewBox="0 0 64 64"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g transform="scale(-1, 1) translate(-64, 0) rotate(-25, 32, 32)">
                    <path fill={color} d="M47.4 42.2c3.9 0 9.5-1.8 12.6-5.9 1.9-2.6 2.5-5.6 1.6-8.7-.8-2.8-2.7-5-5.3-6-3-1.2-6.5-.7-9.9 1.4-1 .6-1.3 1.9-.7 2.9.6 1 1.9 1.3 2.9.7 2.3-1.4 4.5-1.8 6.2-1.1 1.4.5 2.4 1.7 2.8 3.2.5 1.9.2 3.6-.9 5.1-2.1 2.7-6.3 4.2-9.2 4.2-1.2 0-2.1.9-2.1 2.1-.1 1.2.9 2.1 2 2.1z"></path>
                    <path d="M54.9 40.4c-2.5 1.2-5.3 1.8-7.5 1.8-1.2 0-2.1-.9-2.1-2.1 0-1.2.9-2.1 2.1-2.1 1.7 0 3.9-.5 5.8-1.5.6 1.5 1.1 2.8 1.7 3.9zm-9.3-14.5c-.6-1-.3-2.3.7-2.9 1.3-.8 2.7-1.4 4-1.7 0 1.3.1 2.8.3 4.3-.7.2-1.4.6-2.1 1-1 .6-2.3.3-2.9-.7z" opacity=".1"></path>
                    <path fill={color} d="M21.1 42.6c.4-1.4 2-7.5 2.4-9-5.2-1.6-9.7-4.4-11.5-9.5-1.4.5-2.8 1-4.3 1.5 1.4 7.7 6.8 15 13.4 17z"></path>
                    <path d="M23.5 33.6c-.4 1.4-2 7.5-2.4 9-2.5-.8-4.9-2.3-6.9-4.3 1.9-1.7 3.2-4.3 3.4-7.3 1.7 1.1 3.7 2 5.9 2.6z" opacity=".1"></path>
                    <path fill="#abe5ff" d="M10.9 27.7c2.6-.9 4.3-3.3 4.5-6l.1-1.4c.1-1.8-1.7-3.2-3.4-2.6l-8.3 2.9c-1.7.6-2.3 2.8-1 4.2l1 1c1.7 2.1 4.5 2.9 7.1 1.9z"></path>
                    <path d="m15.5 20.4-.1 1.4c-.2 2.7-1.9 5.1-4.5 6-2.6.9-5.4.2-7.3-1.8l-.5-.6c5.1.9 10.7-1.4 12.1-6.4.2.4.3.9.3 1.4z" opacity=".1"></path>
                    <path fill={color} d="M24.7 47.9h19.2c4 0 7.1-3.6 6.5-7.6l-2.7-18.2c-.5-3.2-3.3-5.6-6.5-5.6H27.4c-3.3 0-6.1 2.4-6.5 5.6l-2.7 18.2c-.7 4 2.4 7.6 6.5 7.6z"></path>
                    <path fill="#abe5ff" d="M45.2 16.1H23.4c-1.4 0-2.5 1.1-2.5 2.5v3.5h26.8v-3.5c0-1.4-1.2-2.5-2.5-2.5zm-6.5 20.3c0 3.6-2.4 4.8-4.4 4.8-2 0-4.4-1.2-4.4-4.8 0-3.6 3.3-8.1 4.4-8.1 1.1 0 4.4 4.6 4.4 8.1z"></path>
                    <path d="M43.9 47.9H35C42.7 43 47.1 30 42.1 16.1h3c1.4 0 2.5 1.1 2.5 2.5v3.5l2.7 18.2c.7 4-2.4 7.6-6.4 7.6z" opacity=".1"></path>
                  </g>
                </svg>
                
                {/* Water Drops - falling from spout onto tree */}
                <div className="water-drops-container">
                  {[...Array(numDrops)].map((_, i) => (
                    <div
                      key={i}
                      className="water-drop"
                      style={{
                        backgroundColor: color,
                        animationDelay: `${i * 0.3}s`,
                        left: `${30 + (i % 3) * 8}px`,
                        opacity: 0.7
                      }}
                    />
                  ))}
                </div>

                {/* Custom Tooltip */}
                {hoveredCan === index && (
                  <div className="watering-can-tooltip">
                    <strong>{formatTypeName(type)}</strong>
                    <span>{count}</span>
                  </div>
                )}
                <div className="drop-container">
                  {[...Array(numDrops)].map((_, i) => (
                    <div
                      key={i}
                      className="drop"
                      style={{
                        backgroundColor: color,
                        left: `${15 + (i % 7) * 12}px`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${1.5 + (i % 3) * 0.3}s`
                      }}
                    />
                  ))}
                </div>
              </div>
              
              
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WateringCanV2;